// breather/main.tsx — v0.3 Sensory Guidance Breathing Exercise
//
// -----------------------------------------------------------------------------
// This is the app entry point.
// It is the glue between three domains:
//
// 1) DOM + UX
//    - Intro overlay, “Start” / “Run Again” buttons, and the centered coach text.
//
// 2) Breath timing
//    - Sequencer reads a JSON breathing pattern (4-7-8) and emits phase events.
//    - main.tsx translates those phase events into visual targets.
//
// 3) Visual state
//    - Blob uniforms (shape, band position, idle wobble) are tweened via GSAP.
//    - LightingRig halo is tinted and faded in sync with phases.
//    - PostFX handles bloom and post-processing.
//
// Design intent:
// - This file should read like a “story of the runtime” for a new developer.
// - If you are changing the look and feel, start with the phase presets (NEUTRAL / PEAK_TOP / PEAK_BOTTOM)
//   and the phase tween function that consumes them.
// -----------------------------------------------------------------------------
//
// Architecture + UX Overview
//
// Runtime loop
// - Engine owns requestAnimationFrame, timekeeping, resize hooks, and a single tick() entry point.
// - Every frame: Engine -> SceneGraph.update(dt) -> renderer/composer render.
//
// Scene objects
// - SceneGraph wires together Three.js scene/camera/renderer and holds the core nodes.
// - Blob is the shader-driven breathing organism (uniforms control band/glow/deformation).
// - LightingRig controls key/rim/halo-style lighting and phase tinting.
// - PostFX configures optional bloom/tonemap/composer pipeline (if enabled).
//
// Sequencer (4-7-8)
// - Sequencer reads the 4-7-8 JSON and emits events like phaseStart / cycleEnd / sessionEnd.
// - main.tsx listens to those events to drive: coach text + uniform/lighting targets per phase.
//
// Current UX choices
// - One primary coach (bigCoach) 
// - Phase countdown (coach-secondary) fades out early and stays at opacity 0, creating a natural quiet gap between phases.
// - Intro overlay: blob starts smaller/softer, then reveals into the full experience on Start.
//
// Notes for future edits
// - Keep all phase timing in one place (Sequencer), and treat coach timing constants as product-feel knobs.
//
import { Engine } from './core/Engine'
import { SceneGraph } from './scene/SceneGraph'
import { Blob } from './scene/Blob'
import { LightingRig } from './scene/LightingRig'
import { Sequencer } from './core/Sequencer'
import type { PhaseDef } from './core/Sequencer'
import seq478  from './data/sequences/478.json'
import seqBox  from './data/sequences/box.json'
import seqExhale from './data/sequences/exhale.json'
import seqRelax from './data/sequences/relax.json'
import modes from './data/modes.json'
import gsap from 'gsap'
import { mountDevPlayground } from './devPlayground'
import * as THREE from 'three'
import { Particles } from './scene/Particles'

const PATTERNS = {
  '478':   seq478,
  'box':   seqBox,
  'exhale': seqExhale,
  'relax': seqRelax,
} as const

type PatternKey = keyof typeof PATTERNS

const PALETTE = {
  inhale:    new THREE.Color('#8C7FFF'), // Light periwinkle violet
  hold:      new THREE.Color('#6E5BEF'), // Mid-tone balanced violet
  exhale:    new THREE.Color('#5D4AD6'), // Rich warm-leaning violet
  exhaleEnd: new THREE.Color('#6342c0'), // Same family; safe default for tail blend
} as const

// -----------------------------------------------------------------------------
// CONFIG (tuning knobs / art direction)
// Keep all “change the feel” parameters in this one object.
// Search for CONFIG. to tune the app.
// -----------------------------------------------------------------------------
type Preset = {
  elongate: number
  bulgeMax: number
  lightY: number
  idleAmp: number
  airWidth: number
  airWeight: number
}

// (CONFIGURALE SETTINGS) Blob Visual States (phase targets)
const PRESETS = {
  NEUTRAL: {
    elongate: 0.0,
    bulgeMax: 0.18,
    lightY: 0.1,
    idleAmp: 0.058,
    airWidth: .25,
    airWeight: 0.40
  },
  PEAK_TOP: {
    elongate: 0.99,
    bulgeMax: 0.25,
    lightY: 0.10,
    idleAmp: 0.009,
    airWidth: .35,
    airWeight: 0.46
  },
  PEAK_BOTTOM: {
    elongate: -0.75,
    bulgeMax: 0.20,
    lightY: 0.10,
    idleAmp: 0.018,
    airWidth: .35,
    airWeight: 0.46
  }
} satisfies Record<string, Preset>

const CONFIG = {
  // ---------------------------------------------------------------------------
  // Blob
  // ---------------------------------------------------------------------------
  blob: {
    // Blob mesh scale targets (phase endpoints)
    scales: {
      inhale:       { xz: 0.490, y: 0.590 },
      exhaleBottom: { xz: 0.350, y: 0.450 },
      // Used for the intro reveal landing. Keep this aligned with the early-session feel.
      full:         { xz: 0.490, y: 0.590 },
    },

    // Intro choreography (how the blob arrives before the first real exhale)
    intro: {
      // Start lower so the reveal feels like it rises up and in
      offsetY: -0.45,

      // Intro starts blob-free. Alpha is applied after uniforms exist.
      alpha: 0,

      // Delay before alpha/scale begin (lets the intro fade start first)
      delay: 0.5,

      // 0..1 between exhaleBottom and inhale (land semi-full so the first exhale has room)
      fullness: 0.72,

      // Durations (seconds)
      riseT:  2.8, // y offset back to 0
      alphaT: 1.9, // blob alpha to 1
      scaleT: 4.8, // scale up to intro land scale

      // Pre-exhale: the first coached exhale before the sequencer begins
      preExhaleDur: 7,

      // Let the coach message breathe before the pre-exhale begins
      coachLeadHold: .5,
    },

    // Wobble gain. Keep consistent across phases to avoid a visible "jump."
    wobbleGain: 1.5,
  },

  // ---------------------------------------------------------------------------
  // Shader polish multipliers (relative to current shader defaults)
  // ---------------------------------------------------------------------------
  polish: {
    // The idle state mostly shows after the exercise.
    idleNoiseSpeedMult: 2.35,

    // Rim tightness. Lower values make the blob more opaque.
    exhaleFresnelMult: 0.75,
    calmFresnelMult:   0.99,

    // Bottom glow
    holdBloomMult: 1.35,

    // Keep drift speed consistent so wobble does not "freeze" on start
    activeNoiseSpeedMult: 1.95,
  },

  // ---------------------------------------------------------------------------
  // Phase target presets (visual states)
  // ---------------------------------------------------------------------------
  presets: PRESETS,

  // ---------------------------------------------------------------------------
  // Coach timing and pacing (how calm the UI feels)
  // ---------------------------------------------------------------------------
  coach: {
    phaseLabel: {
      fadeIn:  1.0,  // how long the phase label takes to appear
      hold:    0.45, // how long it stays fully visible before it starts fading
      fadeOut: 1.0,  // how long it takes to fade away
    },
    countdown: {
      fadeIn:  0.9, // how long the instruction + digits take to appear
      fadeOut: 1.2, // how long they take to fade away (starts near the end of the phase)
      gap:     0.3, // how long they stay fully invisible before the next phase begins
    },
  },

  // ---------------------------------------------------------------------------
  // Halo (opacity and intensity)
  // ---------------------------------------------------------------------------
  halo: {
    inhaleOpacity: 0.03,
    holdOpacity:   0.02,
    otherOpacity:  0.0,

    // Exhale: visible during exhale, then fully off at the bottom
    exhaleOpacity:         0.02,
    exhaleIntensityMax:    0.6,
    exhaleIntensityScale:  0.35,
    exhaleTailDelayFactor: 0.72,
    exhaleTailDurMin:      0.15,
    exhaleTailDurScale:    0.28,
  },
} as const


// Math helper used for soft transitions.
// We keep a local smoothstep so we can tune UI fades and shader-driving scalars without
// pulling additional math dependencies into this entry file.
const smoothstep = (a: number, b: number, x: number) => {
  const t = THREE.MathUtils.clamp((x - a) / (b - a), 0, 1)
  return t * t * (3 - 2 * t)
}


/* ---------------------------------------
   DOM elements (HTML overlay)
---------------------------------------- */
// This app is primarily a WebGL scene, but it is wrapped in a minimal DOM UI.
// These references are the bridge between “app state” and what the user sees
// before and during the session.
//
// Required markup:
// - .frame: the container Engine attaches the renderer to
// - #intro: the intro overlay card
// - #start / #again: primary entry and restart actions
// - #bigCoach: centered text coach (phase labels + countdown in guided mode)
//
// If any of these are missing in index.html, we want a loud failure (null access)
// rather than silently doing nothing.

const frame = document.querySelector('.frame') as HTMLElement
const intro = document.getElementById('intro') as HTMLDivElement
// startBtn is no longer used (replaced by per-card Start buttons in the pattern grid).
// Kept as a nullable reference so dev-mode code that hides it does not crash.
const startBtn = document.getElementById('start') as HTMLButtonElement | null
const againBtn = document.getElementById('again') as HTMLButtonElement
const bigCoach = document.getElementById('bigCoach') as HTMLDivElement
const bigCoachCard = bigCoach.querySelector('.big-coach__card') as HTMLDivElement
const coachSecondary = document.getElementById('coachSecondary') as HTMLDivElement | null

// Primary coach animation handles (prevents overlapping phase transitions)
let primaryCoachTween: gsap.core.Tween | null = null
let primaryCoachFadeTl: gsap.core.Timeline | null = null


// --- AUDIO -----------------------------------------------------------------------
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

// Master trim for mobile (tune this). Desktop stays at 1.0.
const MOBILE_GAIN = isMobile ? 1.0 : 1.0

const primeDone = new WeakMap<HTMLAudioElement, Promise<void>>()

function setVol(a: HTMLAudioElement, baseVol: number) {
  // Store the "truth" volume (unscaled). armAndPlay applies MOBILE_GAIN at play-time.
  ;(a as any).__baseVol = baseVol
}

async function armAndPlay(a: HTMLAudioElement) {
  const pending = primeDone.get(a)
  if (pending) await pending

  const base = (a as any).__baseVol ?? a.volume
  const vol = base * MOBILE_GAIN

  a.muted = false
  a.volume = vol
  a.currentTime = 0
  return a.play()
}

// --- Start chime (plays once on session start) ---
const startChime = new Audio('sounds/bell-soft.mp3')
startChime.preload = 'auto'
setVol(startChime, .5)

let startChimePlayed = false

function playStartChimeOnce() {
  if (startChimePlayed) return
  startChimePlayed = true

  try {
    startChime.pause()
    startChime.currentTime = 0
    void armAndPlay(startChime).catch(() => {})
  } catch {}
}

// --- Session audio layers (tweak to taste) ---
const sessionBase = new Audio('sounds/session-base.mp3')
sessionBase.preload = 'auto'
sessionBase.loop = true
setVol(sessionBase, 0.25)

const inhaleCue = new Audio('sounds/inhale.mp3')
inhaleCue.preload = 'auto'
setVol(inhaleCue, 0.15)

const exhaleCue = new Audio('sounds/exhale.mp3')
exhaleCue.preload = 'auto'
setVol(exhaleCue, .15)

exhaleCue.addEventListener('error', () => {
    const err = (exhaleCue as any).error
  console.log('[EXHALE ERROR]', err?.code, err?.message || err)
})
exhaleCue.addEventListener('canplay', () => console.log('[EXHALE canplay]'))
exhaleCue.addEventListener('playing', () => console.log('[EXHALE playing]'))
exhaleCue.addEventListener('pause', () => console.log('[EXHALE pause]'))

// --- Mobile audio unlock (iOS Safari / Chrome iOS) ---
// iOS only "unlocks" the specific media elements that successfully play during a user gesture.
// Prime each cue silently on Start so later plays are not blocked.
function primeAudioSilently(a: HTMLAudioElement) {
  const existing = primeDone.get(a)
  if (existing) return existing

  a.muted = true
  a.volume = 0

  let done: Promise<void>

  try {
    a.currentTime = 0
    const p = a.play()

    const finish = () => {
      try {
        a.pause()
        a.currentTime = 0
        // stay silent after priming
        a.muted = true
        a.volume = 0
      } catch {}
    }

    done =
      p && typeof (p as any).then === 'function'
        ? (p as Promise<void>).then(finish).catch(() => {})
        : Promise.resolve().then(finish)
  } catch {
    done = Promise.resolve()
  }

  primeDone.set(a, done)
  return done
}

let audioPrimed = false
function primeAllAudioOnce() {
  if (audioPrimed) return
  audioPrimed = true

  // Prime only cues that must play later outside the initial user gesture.
  // Do NOT prime startChime (it plays on the Start click), and do NOT prime sessionBase.
  primeAudioSilently(inhaleCue)
  primeAudioSilently(exhaleCue)
}

let sessionBaseStarted = false

function startSessionBaseOnce() {
  if (sessionBaseStarted) return
  sessionBaseStarted = true

  try {
    sessionBase.pause()
    sessionBase.currentTime = 0
    void armAndPlay(sessionBase).catch(() => { /* ignore if blocked */ })
  } catch {
    // ignore
  }
}

let firstInhale = true

function playInhaleCue() {
  try {
    inhaleCue.pause()
    inhaleCue.currentTime = 0

    if (firstInhale) {
      firstInhale = false
      setTimeout(() => {
        void armAndPlay(inhaleCue).catch(() => {})
      }, 120)
      return
    }

    void armAndPlay(inhaleCue).catch(() => {})
  } catch {
    // ignore
  }
}

function playExhaleCue() {
  try {
    exhaleCue.pause()
    exhaleCue.currentTime = 0

    console.log(
      '[EXHALE PLAY]',
      exhaleCue.src,
      'muted=',
      exhaleCue.muted,
      'vol=',
      exhaleCue.volume
    )

    const p = armAndPlay(exhaleCue)

    if (p && typeof (p as any).catch === 'function') {
      ;(p as Promise<void>).catch((e) => console.log('[EXHALE play reject]', e))
    }
  } catch {}
}



function stopSessionBase() {
  try {
    sessionBase.pause()
    sessionBase.currentTime = 0
  } catch {
    // ignore
  }
  sessionBaseStarted = false
}

// ---------------------------------------
// Mode selection
// ---------------------------------------
// Modes live in data/modes.json.
// A mode selects product-feel defaults such as:
// - number of cycles in a session
// - whether the session is guided (practice rep + countdown text)
// - any future per-mode variations (themes, pacing, accessibility)
//
// Query params:
// - ?mode=<key> chooses a named mode from modes.json
// - ?dev=1 or #dev enables the dev playground and disables the intro UX
// ---------------------------------------
type ModeKey = keyof typeof modes

const url = new URL(window.location.href)
const modeParam = url.searchParams.get('mode')

const isDevPlayground =
  url.searchParams.get('dev') === '1' ||
  url.hash === '#dev'

const availableModeKeys = Object.keys(modes) as ModeKey[]
const fallbackModeKey = availableModeKeys[0]

const modeKey: ModeKey =
  modeParam && (modeParam in modes)
    ? (modeParam as ModeKey)
    : fallbackModeKey

const mode = modes[modeKey]


// ---------------------------------------
// Dev mode
// ---------------------------------------
// Dev mode is meant for shader and motion iteration.
// The priorities shift:
// - Keep the WebGL scene stable (no intro overlay, no blur changes).
// - Hide the start/restart UI because the dev panel drives state.
// - Still mount everything so the full runtime path is exercised.
// ---------------------------------------
const skipIntroIfDev = () => {
  if (!isDevPlayground) return

  // Hide the whole intro card immediately so nothing shifts or blurs behind it
  intro.classList.add('hidden')
  intro.style.opacity = '0'
  intro.style.display = 'none'
  intro.style.pointerEvents = 'none'

  // In dev mode, remove start/again UI, we’ll drive behavior from the dev panel
  if (startBtn) startBtn.style.display = 'none'
  againBtn.style.display = 'none'
}

skipIntroIfDev()

/* ---------------------------------------
   Engine + Scene Setup
---------------------------------------- */
// Construction order matters:
// - Engine owns the render loop and knows the DOM container (frame).
// - SceneGraph is built on top of Engine and creates scene/camera/renderer.
// - LightingRig attaches lighting nodes to the graph.
// - Blob creates the central mesh + shader material and adds it to the graph.
//
// main.tsx does not manipulate raw Three.js objects directly unless it is part
// of choreography (GSAP tweens, scale reveals, etc). SceneGraph and the scene/
// classes keep the low-level Three.js concerns contained.

const engine = new Engine(frame)
const graph = new SceneGraph(engine)
const lighting = new LightingRig(graph)
const blob = new Blob(graph)
const particles = new Particles(graph)
particles.setPhase('hold', 1)

// Blob mesh scale targets (CONFIGURALE SETTINGS)
// - INTRO: slightly smaller + calmer presence behind the intro overlay
// - INHALE/HOLD: "full" breathing volume (tuned in dev playground)
// - EXHALE_BOTTOM: smallest point at the bottom of the exhale
// Intro scale is intentionally smaller so the blob feels “present but not demanding” behind the card.
// Note: If your editor warns this is unused, that is because dev mode may bypass intro choreography.
// Keep it anyway, it documents the intended intro feel and makes it easy to re-enable if needed.

// These control the blob shape and size at the top of the inhale and the bottom of the exhale
const INHALE_BLOB_SCALE = CONFIG.blob.scales.inhale
const EXHALE_BOTTOM_BLOB_SCALE = CONFIG.blob.scales.exhaleBottom

// Used for the intro → session reveal (match INHALE so the first phase doesn't "pop")
const FULL_BLOB_SCALE = CONFIG.blob.scales.full

const INTRO_BLOB_OFFSET_Y = CONFIG.blob.intro.offsetY // start lower so the reveal scales "up and in"
// If intro is visible (non-dev), start the blob smaller for a reveal feel.
if (!isDevPlayground) {
  blob.mesh.scale.set(0.001, 0.001, 0.001)
  // Outside the exercise, let the blob feel a little more alive.
  // (Applied later, after uniform helpers initialize.)
}

/* ---------------------------------------
   Extra polish uniforms (optional)
   We only touch these if the corresponding uniform exists.
---------------------------------------- */
const U = blob.uniforms as any

// Intro starts blob-free: set alpha after uniforms exist (scale already ~0)
// (CONFIGURABLE SETTINGS)
setBlobAlpha(CONFIG.blob.intro.alpha)

const BASE = {
  noiseSpeed:   U.uNoiseSpeed?.value ?? 1,
  fresnelPower: U.uFresnelPower?.value ?? 1,
  bloomBoost:   U.uBloomBoost?.value ?? 0.5, // this doesn't appear to do anything
}

// (CONFIGURALE SETTINGS) Tuning knobs (multipliers relative to current shader defaults) 
const IDLE_NOISE_SPEED_MULT = CONFIG.polish.idleNoiseSpeedMult // The idle state mostly shows after the exercise. During the intro the idle state is only shown for a fraction of a second, it's almost imperceptable.
const EXHALE_FRESNEL_MULT   = CONFIG.polish.exhaleFresnelMult // Affects Rim-tightness - lower valuse make the blob more opaque
const CALM_FRESNEL_MULT     = CONFIG.polish.calmFresnelMult // Affects Rim-tightness - lower valuse make the blob more opaque
const HOLD_BLOOM_MULT       = CONFIG.polish.holdBloomMult //Affects bottom glow

let exhaleHaloTail: gsap.core.Tween | null = null

function tweenUniformValue(
  uniformName: string,
  value: number,
  duration: number,
  ease: gsap.EaseString
) {
  const u = (blob.uniforms as any)[uniformName]
  if (!u) return
  gsap.to(u, { value, duration, ease, overwrite: true })
}

function setIdleUniforms() {
  // Faster noise drift outside the exercise (intro + post-session idle)
  tweenUniformValue('uNoiseSpeed', BASE.noiseSpeed * IDLE_NOISE_SPEED_MULT, 2.2, 'sine.inOut')
}

// Apply idle look on initial load (intro screen)
if (!isDevPlayground) setIdleUniforms()

// Seed the intro with the same "bottom of exhale" polish we normally reach later.
// This is intentionally a one-time init, Sequencer phaseStart will take over once the session begins.
if (!isDevPlayground) {
  // Color at bottom-of-exhale (matches where your exhale tween ultimately drifts)
  gsap.set(blob.colorPhase, {
    r: PALETTE.exhaleEnd.r,
    g: PALETTE.exhaleEnd.g,
    b: PALETTE.exhaleEnd.b,
  })

  // Halo treatment: exhale hue, but at bottom it should be fully faded out
  lighting.updateHaloColor('#' + PALETTE.exhale.getHexString(), 0.0, 0)

  // Post/rim polish: mirror the same targets phaseStart uses for exhale
  tweenUniformValue('uBloomBoost', BASE.bloomBoost, 0, 'sine.inOut')
  tweenUniformValue('uFresnelPower', BASE.fresnelPower * EXHALE_FRESNEL_MULT, 0, 'sine.inOut')
}


function setActiveUniforms() {
  // Keep the same drift speed we use during idle/intro so wobble does not “freeze” on start
  tweenUniformValue('uNoiseSpeed', BASE.noiseSpeed * CONFIG.polish.activeNoiseSpeedMult, 0.8, 'sine.inOut') // was 1.95, 0.8
}


// Blob visibility helpers (intro reveal)
// We try uniforms first (shader alpha), then fall back to material opacity if present.
function setBlobAlpha(a: number) {
  const v = Math.max(0, Math.min(1, a))
  if (U.uOpacity)      { U.uOpacity.value = v; return }
  if (U.uAlpha)        { U.uAlpha.value = v; return }
  if (U.uGlobalAlpha)  { U.uGlobalAlpha.value = v; return }
  const mat: any = (blob.mesh as any).material
  if (mat && 'opacity' in mat) {
    mat.transparent = true
    mat.opacity = v
  }
}

function tweenBlobAlpha(to: number, duration: number, ease: string) {
  // Uniform tweens need a wrapper object because gsap can't tween nested getter/setter reliably
  if (U.uOpacity || U.uAlpha || U.uGlobalAlpha) {
    const key = U.uOpacity ? 'uOpacity' : (U.uAlpha ? 'uAlpha' : 'uGlobalAlpha')
    const obj = { a: (U as any)[key].value as number }
    return gsap.to(obj, {
      a: to,
      duration,
      ease,
      overwrite: true,
      onUpdate: () => { (U as any)[key].value = obj.a }
    })
  }
  const mat: any = (blob.mesh as any).material
  if (mat && 'opacity' in mat) {
    mat.transparent = true
    return gsap.to(mat, { opacity: to, duration, ease, overwrite: true })
  }
  // If no alpha path exists, no-op (scale still handles intro invisibility).
  return gsap.to({}, { duration: 0 })
}

// -----------------------------------------------------------------------------
// Render loop
// -----------------------------------------------------------------------------
// Engine.start registers a per-frame callback.
// Inside it we:
// - advance blob time-dependent uniforms (uTime, idle motion, noise drift)
// - render the SceneGraph through the PostFX composer (bloom/tone-mapping)
//
// Breath phase changes do NOT happen here.
// They are event-driven by Sequencer and animated via GSAP tweens that run
// independently of the render callback.
// -----------------------------------------------------------------------------
engine.start(() => {
  blob.update(engine.dt)
  particles.update(engine.dt)
  graph.render()
})

/* ---------------------------------------
   Blob Visual States (phase targets)
---------------------------------------- */
// (CONFIGURALE SETTINGS)
// These three objects are the “art direction knobs” for breathing.
// Think of them as presets, not formulas.
//
// They represent the visual expression of the blob at key points:
// - NEUTRAL: resting baseline (used for intro + calm end state)
// - PEAK_TOP: full inhale (top bulge, band higher, slightly more motion)
// - PEAK_BOTTOM: full exhale (bottom bulge, band lower, calmer motion)
//
// main.tsx is the source of truth for these targets, as documented in developer-instructions.md.
//
// Important ordering rule:
// - Keep these presets above any function that references them.
//   Moving them below can cause “used before initialization” errors if a refactor
//   turns function declarations into const arrow functions.
// ---------------------------------------- */
const NEUTRAL = CONFIG.presets.NEUTRAL
const PEAK_TOP = CONFIG.presets.PEAK_TOP
const PEAK_BOTTOM = CONFIG.presets.PEAK_BOTTOM

/* ---------------------------------------
   Tween Helpers
---------------------------------------- */
// Everything visual in this app moves via GSAP.
// These helpers exist for two reasons:
//
// 1) Consistency
//    Every phase change uses the same tween paths and easing, which prevents
//    “one-off” motion that feels out of sync.
//
// 2) Safety
//    When a user restarts mid-session or when dev mode scrubs values, we must
//    kill or overwrite old tweens, or else old timelines will keep fighting the
//    new targets.
// ---------------------------------------- */
const killPhaseTweens = () => {
  gsap.killTweensOf([
    (blob.uniforms as any).uElongate,
    (blob.uniforms as any).uBulgeMax,
    (blob.uniforms as any).uLightY,
    (blob.uniforms as any).uIdleAmp,
    (blob.uniforms as any).uAirWidth,
    (blob.uniforms as any).uAirWeight,
    blob.colorPhase,
    blob.mesh.scale
  ])
}

const tweenMeshScale = (
  S: { xz: number; y: number },
  duration: number,
  ease: gsap.EaseString
) => {
  gsap.to(blob.mesh.scale, {
    x: S.xz,
    y: S.y,
    z: S.xz,
    duration,
    ease,
    overwrite: true
  })
}

const tweenUniformBlock = (T: typeof NEUTRAL, duration: number, ease: gsap.EaseString) => {
  gsap.to((blob.uniforms as any).uElongate, { value: T.elongate, duration, ease })
  gsap.to((blob.uniforms as any).uBulgeMax, { value: T.bulgeMax, duration, ease })
  gsap.to((blob.uniforms as any).uLightY,   { value: T.lightY,   duration, ease })
  gsap.to((blob.uniforms as any).uIdleAmp,  { value: T.idleAmp,  duration, ease })
  gsap.to((blob.uniforms as any).uAirWidth, { value: T.airWidth, duration, ease })
  gsap.to((blob.uniforms as any).uAirWeight,{ value: T.airWeight,duration, ease })
}

const snapUniformBlock = (T: typeof NEUTRAL) => {
  gsap.set((blob.uniforms as any).uElongate, { value: T.elongate })
  gsap.set((blob.uniforms as any).uBulgeMax, { value: T.bulgeMax })
  gsap.set((blob.uniforms as any).uLightY,   { value: T.lightY })
  gsap.set((blob.uniforms as any).uIdleAmp,  { value: T.idleAmp })
  gsap.set((blob.uniforms as any).uAirWidth, { value: T.airWidth })
  gsap.set((blob.uniforms as any).uAirWeight,{ value: T.airWeight })
}

const colorTo = (hex: string, duration: number, ease: gsap.EaseString = 'sine.inOut') => {
  const target = new THREE.Color(hex)
  gsap.to(blob.colorPhase, { r: target.r, g: target.g, b: target.b, duration, ease })
}

/* ---------------------------------------
   Phase-driven tweens (Sequencer -> visuals)
---------------------------------------- */
// Sequencer gives us phase IDs: 'inhale' | 'hold' | 'exhale'.
// For each phase, we:
// - tween blob uniforms toward the right preset target
// - tween blob mesh scale toward inhale/exhale sizes
// - tint or fade the halo lighting to match the phase mood
//
// This is where “breathing timing” becomes “breathing appearance.”
// ---------------------------------------- */
// (CONFIGURALE SETTINGS) this one makes the wobble jump if the values are different
const WOBBLE_GAIN = CONFIG.blob.wobbleGain

function idleForPhase(id: string) {
  const base =
    id === 'hold'   ? 0.038 :
    id === 'inhale' ? 0.038 :
    id === 'exhale' ? 0.038 :
    0.038

  return base * WOBBLE_GAIN
}

function tweenToPhaseTarget(phaseId: string, dur: number) {
  if (phaseId === 'inhale') {
    tweenUniformBlock({ ...PEAK_TOP, idleAmp: idleForPhase('inhale') }, dur, 'sine.out')
    tweenMeshScale(INHALE_BLOB_SCALE, dur, 'sine.out')
  } else if (phaseId === 'hold' || phaseId === 'hold-in') {
    // Hold at the top — follows inhale, keep full expanded shape
    tweenUniformBlock({ ...PEAK_TOP, idleAmp: idleForPhase('hold') }, 0.4, 'sine.out')
    tweenMeshScale(INHALE_BLOB_SCALE, 0.35, 'sine.out')
  } else if (phaseId === 'hold-out') {
    // Hold at the bottom — follows exhale, keep the deflated shape
    tweenUniformBlock({ ...PEAK_BOTTOM, idleAmp: idleForPhase('hold') }, 0.4, 'sine.out')
    tweenMeshScale(EXHALE_BOTTOM_BLOB_SCALE, 0.35, 'sine.out')
  } else if (phaseId === 'exhale') {
    tweenUniformBlock({ ...PEAK_BOTTOM, idleAmp: idleForPhase('exhale') }, dur, 'sine.inOut')
    tweenMeshScale(EXHALE_BOTTOM_BLOB_SCALE, dur, 'sine.inOut')
  }
}

/* ---------------------------------------
   Coach overlays (centered)
---------------------------------------- */
// The centered coach is intentionally minimal.
// It stays visible for the entire exercise as a steady, always-available guide (timing + reassurance).
// If you ever want it to fade after a practice rep, this is the place to change that policy.
//
// Implementation notes:
// - Prefer opacity tweens over display toggles during live guidance.
//   Display toggles can cause layout shifts that show through blur.
// - We keep references to active tweens so phase transitions can interrupt cleanly.
// ---------------------------------------- */
// (CONFIGURALE SETTINGS)

const COACH_FADE_IN  = CONFIG.coach.phaseLabel.fadeIn
const COACH_HOLD     = CONFIG.coach.phaseLabel.hold
const COACH_FADE_OUT = CONFIG.coach.phaseLabel.fadeOut

const showPhaseLabel = (label: string) => {
  gsap.killTweensOf(bigCoach)
  bigCoachCard.textContent = label

  gsap.timeline()
    .set(bigCoach, { display: 'grid', opacity: 0 })
    .to(bigCoach, { opacity: 1, duration: COACH_FADE_IN, ease: 'sine.out' })
    .to({},        { duration: COACH_HOLD })
    .to(bigCoach, {
      opacity: 0,
      duration: COACH_FADE_OUT,
      ease: 'sine.inOut',
      onComplete() { gsap.set(bigCoach, { display: 'none' }) }
    })
}

function showPrimaryCoachMessage(text: string, holdSeconds: number) {
  // Kill anything currently driving the coach
  if (primaryCoachTween) { primaryCoachTween.kill(); primaryCoachTween = null }
  if (primaryCoachFadeTl) { primaryCoachFadeTl.kill(); primaryCoachFadeTl = null }
  gsap.killTweensOf(bigCoach)

  // Keep secondary in layout so primary line matches phase positioning
  if (coachSecondary) {
    gsap.killTweensOf(coachSecondary)
    coachSecondary.style.display = 'block'     // occupies grid row
    coachSecondary.style.visibility = 'hidden' // but not visible
    gsap.set(coachSecondary, { opacity: 0 })   // belt and suspenders
  }

  gsap.set(bigCoach, { display: 'grid', opacity: 0 })
  bigCoachCard.textContent = text

  const tl = gsap.timeline()
    .to(bigCoach, { opacity: 1, duration: CONFIG.coach.countdown.fadeIn, ease: 'sine.inOut' })
    .to({}, { duration: holdSeconds })
    .to(bigCoach, { opacity: 0, duration: CONFIG.coach.countdown.fadeOut, ease: 'sine.inOut' })
    .add(() => {
      // restore normal behavior so phase code can show digits normally
      if (coachSecondary) {
        coachSecondary.style.visibility = ''
        coachSecondary.style.display = 'none'
      }
    })

  return tl.then()
}

/**
 * Primary coach countdown (centered): replaces the HUD phase timer.
 *
 * Design goal: keep the UX effortless.
 * - Fade in gently at phase start.
 * - Fade out early, then remain invisible for the remainder of the phase.
 *   That remainder becomes a natural "quiet gap" between phases.
 *
 * Tuned values (current):
 * - COUNT_FADE_IN:  how long it takes the next phase prompt to arrive.
 * - COUNT_FADE_OUT: how quickly the prompt releases.
 * - COUNT_GAP:      how much silent space we leave before the next phase.
 */
// (CONFIGURALE SETTINGS)
const COUNT_FADE_IN  = CONFIG.coach.countdown.fadeIn
const COUNT_FADE_OUT = CONFIG.coach.countdown.fadeOut
const COUNT_GAP      = CONFIG.coach.countdown.gap


// Coach v2 helpers (mockup target: digits above blob, instruction over blob)
// Reads the `copy` field from the phase JSON. Falls back to the label if copy is absent.
const primaryCopyFromPhase = (phase: PhaseDef): string => {
  return phase.copy ?? phase.label ?? phase.id
}


const renderSecondaryDigits = (total: number) => {
  if (!coachSecondary) return
  const nums: number[] = []
  for (let n = Math.max(1, Math.round(total)); n >= 1; n--) nums.push(n)

  // Build spans so we can light up the active digit
  coachSecondary.innerHTML = nums.map((n) => `<span data-n="${n}">${n}</span>`).join(' ')
}

const clearActiveDigit = () => {
  if (!coachSecondary) return
  coachSecondary.querySelectorAll('span.is-active').forEach((el) => el.classList.remove('is-active'))
}

const pulseActiveDigit = (n: number) => {
  if (!coachSecondary) return
  const el = coachSecondary.querySelector(`span[data-n="${n}"]`) as HTMLSpanElement | null
  if (!el) return

  clearActiveDigit()
  el.classList.add('is-active')

  // Micro pulse, subtle
  gsap.killTweensOf(el)
  gsap.fromTo(
    el,
    { scale: 1 },
    { scale: 1.08, duration: 0.16, ease: 'sine.out', yoyo: true, repeat: 1, overwrite: true }
  )

  // Relax back to dim shortly after the beat
  gsap.delayedCall(0.55, () => el.classList.remove('is-active'))
}

const showPrimaryCoachCountdown = (phase: PhaseDef, durationSec: number) => {
  // Kill prior primary-coach animations so nothing can "snipe" display/opacity mid-phase
  if (primaryCoachTween) primaryCoachTween.kill()
  if (primaryCoachFadeTl) primaryCoachFadeTl.kill()
  gsap.killTweensOf(bigCoach)
  gsap.killTweensOf(bigCoachCard)
  if (coachSecondary) gsap.killTweensOf(coachSecondary)

  const total = Math.max(1, Math.round(durationSec))
  const ctrl = { t: 0 }
  let lastActive = -1

  // Primary instruction over blob (bigCoach)
  gsap.set(bigCoach, { display: 'grid', opacity: 0 })
  bigCoachCard.textContent = primaryCopyFromPhase(phase)

  // Secondary digits above blob
  if (coachSecondary) {
    renderSecondaryDigits(total)
    gsap.set(coachSecondary, { display: 'block', opacity: 0 })
    clearActiveDigit()
    pulseActiveDigit(total)
    lastActive = total
  }

  // Drive digit highlight for the full duration (even while faded out)
  primaryCoachTween = gsap.to(ctrl, {
    t: 1,
    duration: durationSec,
    ease: 'linear',
    overwrite: true,
    onUpdate: () => {
      const remaining = Math.max(1, Math.ceil((1 - ctrl.t) * total))
      if (remaining !== lastActive) {
        pulseActiveDigit(remaining)
        lastActive = remaining
      }
    }
  })

  // Fade in immediately, then fade out early and remain invisible for the remainder (the "gap")
  const fadeStart = Math.max(0, durationSec - (COUNT_FADE_OUT + COUNT_GAP))

  primaryCoachFadeTl = gsap.timeline()
    .to(bigCoach, { opacity: 1, duration: COUNT_FADE_IN, ease: 'sine.out' }, 0)
    .to(bigCoach, { opacity: 0, duration: COUNT_FADE_OUT, ease: 'sine.inOut' }, fadeStart)

  if (coachSecondary) {
    primaryCoachFadeTl
      .to(coachSecondary, { opacity: 1, duration: COUNT_FADE_IN, ease: 'sine.out' }, 0)
      .to(coachSecondary, { opacity: 0, duration: COUNT_FADE_OUT, ease: 'sine.inOut' }, fadeStart)
  }

  // IMPORTANT: no display:none here. Opacity handles the gap cleanly.
}

/* ---------------------------------------
   Sequencer Setup
---------------------------------------- */
// Sequencer is a timing engine, not a visual engine.
// It reads the 4-7-8 pattern JSON and emits events:
//
// - phaseStart(phase)
// - phaseTick(phase, t)
// - phaseEnd(phase)
// - cycleEnd
// - sessionEnd
//
// main.tsx subscribes to those events and translates them into:
// - coach text cues (guided mode)
// - GSAP tweens for blob uniforms and mesh scale
// - halo lighting cues via LightingRig
// ---------------------------------------- */
snapUniformBlock(PEAK_BOTTOM)

// Keep intro wobble amplitude.
// PEAK_BOTTOM’s idleAmp is tuned for active breath phases and can read “off” during the intro reveal,
// so we restore the intro baseline just until the sequencer begins (phaseStart will override it).
if ((blob.uniforms as any).uIdleAmp) {
  gsap.set((blob.uniforms as any).uIdleAmp, { value: NEUTRAL.idleAmp })
}

// activeSeq is created when the user clicks a card's Start button.
// It is set to a new Sequencer instance with the selected pattern.
// Defaults to 478 so dev mode and any fallback path still work.
let activeSeq: Sequencer = new Sequencer(PATTERNS['478'], {
  cyclesOverride: mode.cycles,
  guidedMode: mode.guidedMode,
})

// Wire up all Sequencer event listeners to a given seq instance.
// Called each time a new session starts so the new seq gets the same handlers.
function wireSequencer(seq: Sequencer) {
  seq.on('phaseStart', ({ phase }) => {
    const d = phase.duration

    console.log('[particles] phaseStart', phase.id, 'd=', d)

    killPhaseTweens()

    particles.setPhase(phase.id, d)

    if (phase.id === 'inhale') {
      playInhaleCue()
    } else if (phase.id === 'exhale') {
      playExhaleCue()
    }

    // --- Post / Rim polish ---
    // Bloom: give hold a subtle "presence" boost
    if (phase.id === 'hold' || phase.id === 'hold-in') {
      tweenUniformValue(
        'uBloomBoost',
        BASE.bloomBoost * HOLD_BLOOM_MULT,
        Math.min(0.15, d),
        'sine.out'
      )
    } else {
      tweenUniformValue(
        'uBloomBoost',
        BASE.bloomBoost,
        0.55,
        'sine.inOut'
      )
    }

    // Fresnel: quiet on inhale/hold, tight rim at the end of exhale
    if (phase.id === 'exhale') {
      tweenUniformValue(
        'uFresnelPower',
        BASE.fresnelPower * EXHALE_FRESNEL_MULT,
        d,
        'sine.inOut'
      )
    } else {
      tweenUniformValue(
        'uFresnelPower',
        BASE.fresnelPower * CALM_FRESNEL_MULT,
        0.6,
        'sine.inOut'
      )
    }

    // —— COLOR & HALO ——
    if (phase.id === 'exhale') {
      const holdCol = new THREE.Color(
        blob.colorPhase.r,
        blob.colorPhase.g,
        blob.colorPhase.b
      )
      const exCol = PALETTE.exhale
      const endCol = PALETTE.exhaleEnd

      const ctrl = { t: 0 }

      gsap.to(ctrl, {
        t: 1,
        duration: d,
        ease: 'sine.inOut',
        overwrite: true,
        onUpdate: () => {
          const a = smoothstep(0.0, 0.25, ctrl.t)
          const b = smoothstep(0.7, 1.0, ctrl.t)

          const r1 = THREE.MathUtils.lerp(holdCol.r, exCol.r, a)
          const g1 = THREE.MathUtils.lerp(holdCol.g, exCol.g, a)
          const b1 = THREE.MathUtils.lerp(holdCol.b, exCol.b, a)

          blob.colorPhase.r = THREE.MathUtils.lerp(r1, endCol.r, b)
          blob.colorPhase.g = THREE.MathUtils.lerp(g1, endCol.g, b)
          blob.colorPhase.b = THREE.MathUtils.lerp(b1, endCol.b, b)
        },
      })

      const exHex = '#' + exCol.getHexString()

      lighting.updateHaloColor(
        exHex,
        CONFIG.halo.exhaleOpacity,
        Math.min(
          CONFIG.halo.exhaleIntensityMax,
          d * CONFIG.halo.exhaleIntensityScale
        )
      )

      if (typeof exhaleHaloTail !== 'undefined' && exhaleHaloTail) {
        exhaleHaloTail.kill()
        exhaleHaloTail = null
      }

      exhaleHaloTail = gsap.delayedCall(
        d * CONFIG.halo.exhaleTailDelayFactor,
        () => {
          lighting.updateHaloColor(
            exHex,
            0.000,
            Math.max(
              CONFIG.halo.exhaleTailDurMin,
              d * CONFIG.halo.exhaleTailDurScale
            )
          )
        }
      )
    } else {
      const phaseColor = colorForPhase(phase.id)

      gsap.to(blob.colorPhase, {
        r: phaseColor.r,
        g: phaseColor.g,
        b: phaseColor.b,
        duration: d,
        ease: 'sine.inOut',
        overwrite: true,
      })

      const haloOpacity =
        phase.id === 'inhale'
          ? CONFIG.halo.inhaleOpacity
          : (phase.id === 'hold' || phase.id === 'hold-in' || phase.id === 'hold-out')
            ? CONFIG.halo.holdOpacity
            : CONFIG.halo.otherOpacity

      lighting.updateHaloColor(
        '#' + phaseColor.getHexString(),
        haloOpacity,
        d
      )
    }

    tweenToPhaseTarget(phase.id, d)

    if (mode.guidedMode) {
      showPrimaryCoachCountdown(phase, d)
    }
  })

  seq.on('cycleEnd', ({ cycle }) => {
    currentCycle = cycle + 1
  })

  seq.on('sessionEnd', () => {
    againBtn.style.display = 'inline-flex'
    if (!isDevPlayground) {
      settleToIdle(0.85)
    }
  })
}

wireSequencer(activeSeq)

let currentCycle = 1

function colorForPhase(id: string) {
  if (id === 'inhale') return PALETTE.inhale
  if (id === 'hold' || id === 'hold-in' || id === 'hold-out') return PALETTE.hold
  return PALETTE.exhale
}

// ---------------------------------------
// Legacy HUD phase counter (upper-left)
// Disabled. We keep the element hard-hidden to prevent regressions.
// ---------------------------------------
/*. const phaseCoachInlineEl =
  document.getElementById('phaseCoachInline') as HTMLDivElement | null

if (phaseCoachInlineEl) {
  phaseCoachInlineEl.style.display = 'none'
  phaseCoachInlineEl.style.opacity = '0'
  phaseCoachInlineEl.style.pointerEvents = 'none'
} */

// ---------------------------------------
// Session-end settle (prevents “reverse unwind” wobble)
// ---------------------------------------
// The unwind happens when we:
// 1) kill phase tweens, and
// 2) snap uniforms back to idle immediately (or via setIdleUniforms).
//
// Instead, we tween back to the idle baseline over a short settle window,
// while also canceling any delayed halo tail from exhale.
const settleToIdle = (settleDur = 0.85) => {
  // Cancel any delayed exhale tail that might still fire after the session ends.
  if (typeof exhaleHaloTail !== 'undefined' && exhaleHaloTail) {
    exhaleHaloTail.kill()
    exhaleHaloTail = null
  }

  // Make sure no active tweens are still fighting these targets.
  // (killPhaseTweens() usually covers this, but we target the “usual suspects” explicitly.)
  gsap.killTweensOf(blob.uniforms.uIdleAmp)
  gsap.killTweensOf(blob.uniforms.uElongate)
  gsap.killTweensOf(blob.uniforms.uNoiseScale)
  gsap.killTweensOf(blob.uniforms.uNoiseSpeed)

  // Smoothly return shape bias and wobble to neutral idle.
  // This avoids the “snap back” that reads like a reverse shake.
  if (blob.uniforms.uElongate) {
    gsap.to(blob.uniforms.uElongate, {
      value: NEUTRAL.elongate,
      duration: Math.min(0.70, settleDur),
      ease: 'sine.out',
      overwrite: true,
    })
  }

  if (blob.uniforms.uIdleAmp) {
    gsap.to(blob.uniforms.uIdleAmp, {
      value: NEUTRAL.idleAmp,
      duration: settleDur,
      ease: 'sine.out',
      overwrite: true,
    })
  }

  // Keep noise moving while amplitude settles so it doesn’t “freeze-collapse.”
  // If NEUTRAL doesn’t define these, we just glide to the current value (no-op),
  // which still prevents abrupt jumps if something else changed them.
  if (blob.uniforms.uNoiseSpeed) {
    gsap.to(blob.uniforms.uNoiseSpeed, {
      value:
        (NEUTRAL as any).noiseSpeed ?? blob.uniforms.uNoiseSpeed.value,
      duration: settleDur,
      ease: 'sine.out',
      overwrite: true,
    })
  }

  if (blob.uniforms.uNoiseScale) {
    gsap.to(blob.uniforms.uNoiseScale, {
      value:
        (NEUTRAL as any).noiseScale ?? blob.uniforms.uNoiseScale.value,
      duration: settleDur,
      ease: 'sine.out',
      overwrite: true,
    })
  }

  // Fade halo toward “off” at end so the environment doesn’t pop.
  // (Keeps your phaseStart halo behavior intact, only cleans up the end.)
  // Use a tiny epsilon instead of 0 to avoid any shader/material edge cases.
  lighting.updateHaloColor('#000000', 0.000, Math.min(0.60, settleDur))
}

/* ---------------------------------------
   Start Flow (Simplified Intro)
---------------------------------------- */
// This is the user-facing entry choreography.
// Goals:
// - Fade out intro copy without visual popping.
// - Reveal the blob from “barely there” to “ready to breathe.”
//
// Note: dev mode bypasses this entirely. See skipIntroIfDev().
// ---------------------------------------- */
// Pattern selection: read data-pattern from the clicked Start button's card.
// Falls back to '478' if the attribute is missing or unrecognised.
function resolvePattern(btn: HTMLButtonElement): typeof PATTERNS[PatternKey] {
  const card = btn.closest('[data-pattern]') as HTMLElement | null
  const key = (card?.dataset.pattern ?? '478') as PatternKey
  return PATTERNS[key] ?? PATTERNS['478']
}

// Delegated listener on the intro overlay — catches clicks from any card's Start button.
intro.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest('[data-start]') as HTMLButtonElement | null
  if (!btn) return
  // Re-create the sequencer with the selected pattern, wire it up, then run the start flow.
  activeSeq = new Sequencer(resolvePattern(btn), {
    cyclesOverride: mode.cycles,
    guidedMode: mode.guidedMode,
  })
  wireSequencer(activeSeq)
  runStartFlow(activeSeq)
})

async function runStartFlow(seq: Sequencer) {
  if (isDevPlayground) return

  // Prime all audio elements on the user gesture so later cues are not blocked on iOS
  primeAllAudioOnce()

  // Reset per-session audio flags
  firstInhale = true
  startChimePlayed = false

  // Start audio immediately on user gesture
  startSessionBaseOnce()

  // Entering the experience: return idle-only uniforms back to baseline
  setActiveUniforms()

  const INTRO = CONFIG.blob.intro
  const REVEAL_AT = INTRO.delay
  const REVEAL_T = INTRO.scaleT

  const tl = gsap.timeline()

  // Immediately kill intro UI, do not let it linger
  intro.style.pointerEvents = 'none'
  tl.to(
    intro,
    {
      opacity: 0,
      duration: 0.68,
      ease: 'sine.out',
      onComplete: () => intro.classList.add('hidden'),
    },
    0
  )

  // Ensure blob is not visible during the intro fade
  tl.to({}, { duration: 0, onStart: () => setBlobAlpha(0) }, 0)

  // Start lower so the reveal feels like it rises up and in
  gsap.set(blob.mesh.position, { y: INTRO_BLOB_OFFSET_Y })

  // Land in a semi-full state so we can show a real first exhale.
  // 0..1 between exhaleBottom and inhale.
  const INTRO_FULLNESS = INTRO.fullness
  const INTRO_LAND_SCALE = {
    xz:
      EXHALE_BOTTOM_BLOB_SCALE.xz +
      (INHALE_BLOB_SCALE.xz - EXHALE_BOTTOM_BLOB_SCALE.xz) * INTRO_FULLNESS,
    y:
      EXHALE_BOTTOM_BLOB_SCALE.y +
      (INHALE_BLOB_SCALE.y - EXHALE_BOTTOM_BLOB_SCALE.y) * INTRO_FULLNESS,
  }

  // Bias visuals toward inhale-ready during the reveal, but keep the mesh at INTRO_LAND_SCALE.
  tl.add(() => {
    tweenUniformBlock(
      { ...PEAK_TOP, idleAmp: idleForPhase('inhale') },
      0.01,
      'sine.out'
    )

    gsap.to(blob.colorPhase, {
      r: PALETTE.inhale.r,
      g: PALETTE.inhale.g,
      b: PALETTE.inhale.b,
      duration: 0.8,
      ease: 'sine.out',
      overwrite: true,
    })

    lighting.updateHaloColor('#' + PALETTE.inhale.getHexString(), 0.0, 0.0)
  }, 0)

  const canvasEl = document.querySelector('canvas') as HTMLElement | null
  if (canvasEl) {
    tl.to(
      canvasEl,
      { filter: 'blur(0px)', duration: 0.15, ease: 'sine.out', overwrite: true },
      0
    )
  }

  // Rise into place while the intro fades
  tl.to(
    blob.mesh.position,
    { y: 0, duration: INTRO.riseT, ease: 'sine.out', overwrite: true },
    0
  )

  // Reveal blob: a touch delayed so the UI fade begins first
  tl.add(tweenBlobAlpha(1, INTRO.alphaT, 'sine.out'), REVEAL_AT)

  // Scale in, land in semi-full (so the first exhale has room)
  tl.to(
    blob.mesh.scale,
    {
      x: INTRO_LAND_SCALE.xz,
      y: INTRO_LAND_SCALE.y,
      z: INTRO_LAND_SCALE.xz,
      duration: REVEAL_T,
      ease: 'sine.out',
      overwrite: true,
    },
    REVEAL_AT
  )

  await tl.then()

  // Intro coach, let it land, then do a real exhale using the same phase system (wobble + uniforms).
  const PRE_EXHALE_DUR = INTRO.preExhaleDur

  const coachP = showPrimaryCoachMessage(
    'Begin with a soft Haa\nLet it all out',
    INTRO.coachLeadHold + PRE_EXHALE_DUR
  ).catch(() => {})

  // Let the message breathe after it appears
  await new Promise<void>((r) => setTimeout(r, INTRO.coachLeadHold * 1000))

  // Pre-exhale is not a sequencer phase, so manually trigger the cue.
  playExhaleCue()

  tweenToPhaseTarget('exhale', PRE_EXHALE_DUR)
  await new Promise<void>((r) => setTimeout(r, PRE_EXHALE_DUR * 1000))


  // Ensure the coach promise finishes cleanly (if it is still running)
  await coachP

  // Now start the actual session. First phase is inhale, so it expands cleanly from the exhaled state.
  againBtn.style.display = 'none'
  playStartChimeOnce()
  seq.start()
}

againBtn.addEventListener('click', () => location.reload())

if (isDevPlayground) mountDevPlayground({ blob, lighting })
